import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getTitleColor } from "@/lib/titleColors";
import { DemoCard, DemoResultCard } from "./DemoPanels";

export interface CommGenMockInput {
  message: string;
  type: string;
}
export function createMockCommGenResponse(input: CommGenMockInput) {
  return {
    tool: "communication-generator",
    ...input,
    generatedAt: new Date().toISOString(),
    content: `This is a mocked ${input.type} crafted from your input.`,
    tone: "supportive",
    followUps: [
      "Add a positive reinforcement line",
      "Specify next actionable step",
      "Clarify due date if needed",
    ],
  };
}

export default function CommunicationGeneratorDemo() {
  const toolColor = getTitleColor("communicationgenerator");
  const [message, setMessage] = useState("");
  const [type, setType] = useState("email");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any | null>(null);

  const handleGenerate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!message) return;
    setLoading(true);
    await new Promise((r) => setTimeout(r, 500));
    setResult(createMockCommGenResponse({ message, type }));
    setLoading(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="commgen-demo">
      <form onSubmit={handleGenerate} className="contents">
        <DemoCard
          accent={toolColor}
          testId="demo-form"
          title="Communication Brief"
          footer={
            <>
              <Button
                type="submit"
                size="sm"
                disabled={loading || !message}
                className="relative"
                data-testid="demo-generate-btn"
                style={{
                  background: toolColor,
                  boxShadow: `0 2px 4px -1px ${toolColor}50`,
                }}
              >
                <span className={cn(loading && "opacity-0")}>
                  {loading ? "Generating..." : "Generate"}
                </span>
                {loading && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span
                      className="w-4 h-4 rounded-full border-2 border-primary/30 border-r-primary animate-spin"
                      style={{
                        borderColor: toolColor + "40",
                        borderRightColor: toolColor,
                      }}
                    />
                  </span>
                )}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setMessage("");
                  setResult(null);
                }}
                disabled={loading && !result}
                data-testid="demo-reset-btn"
                style={{ borderColor: toolColor + "60" }}
              >
                Reset
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-2">
            <label
              htmlFor="comm-text"
              className="text-xs font-medium tracking-wide uppercase text-muted-foreground"
            >
              Brief / Context
            </label>
            <Input
              id="comm-text"
              className="bg-background/70 backdrop-blur-sm focus-visible:ring-1"
              placeholder="Remind parents about the science fair and request volunteers"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              style={{ borderColor: toolColor + "40" }}
            />
            <div className="flex justify-end mt-1 text-[10px] font-medium tracking-wide uppercase text-muted-foreground">
              {message.length} chars
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full max-w-xs">
            <label className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
              Type
            </label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger
                className="h-9 bg-background/70 backdrop-blur-sm"
                style={{ borderColor: toolColor + "40" }}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="newsletter">Newsletter Blurb</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </DemoCard>
      </form>
      <DemoResultCard
        accent={toolColor}
        title="Generated"
        loading={loading}
        loadingLabel="Generating..."
        result={result}
      />
    </div>
  );
}
